<template>
  <div class="hotelCalendar">
    <div class="hotelCalendarBox">
      <div class="hotelCalendarWrapper">
        <div
          v-for="(m, mIdx) in showMonths"
          :key="m"
          class="hotelCalendarSlide"
        >
          <div
            v-if="mIdx === 0 && hasPreviousMonth"
            class="pc hotelCalendarPrev"
            title="< 前の月へ"
            @click.prevent.stop="renderPreviousMonth"
          >
            <tk-svg-icon icon="prev" class="icoPrev" />
          </div>
          <div
            v-else-if="hasNextMonth"
            class="pc hotelCalendarNext"
            title="次の月へ >"
            @click.prevent.stop="renderNextMonth"
          >
            <tk-svg-icon icon="next" class="icoNext" />
          </div>

          <p class="yymm">
            {{ months[mIdx].year }}{{ i18n.year }}{{ months[mIdx].month
            }}{{ i18n.month }}
          </p>
          <ul class="day">
            <li
              v-for="(dn, dnIdx) in i18n.dayNames"
              :key="dnIdx"
              :title="dn + '曜日'"
            >
              {{ dn }}
            </li>
          </ul>
          <ul
            v-for="w in getWeekIndices(months[mIdx].days)"
            :key="w"
            class="dd"
          >
            <day
              v-for="(d, idx) in months[mIdx].days.slice(w * 7, w * 7 + 7)"
              :key="
                idx + w * 7 + '-' + months[mIdx].month + '-' + months[mIdx].year
              "
              :day-number="d.dayNumber"
              :date="d.date"
              :starting-date="startingDate"
              :ending-date="endingDate"
              :min-enable-date="minEnableDate"
              :max-enable-date="maxEnableDate"
              :hovering-date="hoveringDate"
              :range="range"
              @mouseover="hoveringDate = d.date"
              @day-clicked="handleDayClick($event)"
            >
            </day>
          </ul>
        </div>
      </div>
    </div>

    <div class="hotelCalendarBtn">
      <a class="hotelBtn" @click.prevent.stop="onConfirmed">
        設定する（{{ diffDays }}泊）
      </a>
    </div>
  </div>
</template>

<script>
import throttle from 'lodash.throttle'
import format from 'date-fns/format'
import getDay from 'date-fns/getDay'
import addDays from 'date-fns/addDays'
import addYears from 'date-fns/addYears'
import compareAsc from 'date-fns/compareAsc'
import parse from 'date-fns/parse'
import parseISO from 'date-fns/parseISO'
import addMonths from 'date-fns/addMonths'
import getDaysInMonth from 'date-fns/getDaysInMonth'
import isWeekend from 'date-fns/isWeekend'
import startOfToday from 'date-fns/startOfToday'
import startOfTomorrow from 'date-fns/startOfTomorrow'
import startOfDay from 'date-fns/startOfDay'
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import TkSvgIcon from '../../atoms/TkIcon/TkSvgIcon.vue'
import Day from './Day.vue'

const defaultI18n = {
  dayNames: ['日', '月', '火', '水', '木', '金', '土'],
  year: '年',
  month: '月',
  departureLabel: '行き出発日',
  arrivalLabel: '帰り出発日',
}

const INVALID_DAY = -1
const MAX_HOURS_TODAY = 14
const SELECT_STARTING_TURN = 0
const SELECT_ENDING_TURN = 1

const getMinEnableDate = () => {
  const now = new Date()
  if (now.getHours() < MAX_HOURS_TODAY) {
    return startOfToday()
  } else {
    return startOfTomorrow()
  }
}

export default {
  name: 'TkHotelCalendar',
  components: {
    Day,
    TkSvgIcon,
  },
  props: {
    range: {
      // single, range
      type: Boolean,
      default: false,
    },
    startDate: {
      default: null,
      type: [String, Date],
    },
    endDate: {
      default: null,
      type: [String, Date],
    },
    // Disable calendar before this.
    minDate: {
      default: () => {
        return getMinEnableDate()
      },
      type: [String, Date],
    },
    // Disable calendar after this, default 1 year
    maxDate: {
      default: () => {
        return addYears(new Date(), 1)
      },
      type: [Date, String, Number],
    },
    showMonths: {
      default: 2,
      type: Number,
    },
    i18n: {
      default: () => defaultI18n,
      type: Object,
    },
    ymdFormat: {
      type: String,
      // https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
      default: 'yyyy/MM/dd',
    },
  },
  data() {
    return {
      hoveringDate: null,
      startingDate: null,
      endingDate: null,
      months: [],
      years: [],
      weeksPerMonth: [...Array(5).keys()],
      currentDate: startOfToday(),
      selectingTurn: null,
      diffDays: 0,
    }
  },
  computed: {
    minEnableDate() {
      const md = this.parseTringDate(this.minDate)
      const selectableDate = getMinEnableDate()
      // check whether minDate is after 'today'
      return compareAsc(md, selectableDate) === 1
        ? startOfDay(md)
        : selectableDate
    },
    maxEnableDate() {
      return compareAsc(this.maxDate, this.minEnableDate === 1)
        ? this.minEnableDate
        : startOfDay(this.maxDate)
    },
    displayStartingDate() {
      return this.startingDate
        ? format(this.startingDate, this.ymdFormat)
        : '年/月/日'
    },
    displayEndingDate() {
      return this.endingDate
        ? format(this.endingDate, this.ymdFormat)
        : '年/月/日'
    },
    hasPreviousMonth() {
      // hasn't before the month of min date
      return (
        this.months[0].year > parseInt(format(this.minEnableDate, 'yyyy')) ||
        this.months[0].month > parseInt(format(this.minEnableDate, 'M'))
      )
    },
    hasNextMonth() {
      const lastActiveMonth = this.months[this.months.length - 1]
      // year of the active month couldn't be after year of the max date
      return (
        lastActiveMonth.year < parseInt(format(this.maxEnableDate, 'yyyy')) ||
        lastActiveMonth.month < parseInt(format(this.maxEnableDate, 'M'))
      )
    },
  },
  watch: {
    startingDate(v) {
      this.$emit('starting-date-changed', v)
    },
    endingDate(v) {
      this.$emit('ending-date-changed', v)
    },
  },
  beforeMount() {
    this.startingDate = this.parseTringDate(this.startDate)
    this.endingDate = this.parseTringDate(this.endDate)
    this.selectingTurn =
      this.startingDate && !this.endingDate
        ? SELECT_ENDING_TURN
        : SELECT_STARTING_TURN
    const firstDateOfMonth = parseISO(
      format(
        this.startingDate ? this.startingDate : this.minEnableDate,
        'yyyy-MM-01'
      )
    )
    this.generateMonths(firstDateOfMonth)
    if (this.startingDate && this.endingDate) {
      this.diffDays = differenceInCalendarDays(
        this.endingDate,
        this.startingDate
      )
    }
  },
  methods: {
    getWeekIndices(days) {
      // because of getDaysOfMonth always a multiple times of 7
      return [...Array(parseInt(days.length / 7)).keys()]
    },
    parseTringDate(d) {
      if (d === null || d === undefined) {
        return null
      }
      return typeof d === 'string' ? parse(d, this.ymdFormat) : startOfDay(d)
    },
    onConfirmed() {
      this.$emit('confirmed', {
        startingDate: this.startingDate,
        endingDate: this.endingDate,
      })
    },
    handleDayClick(event) {
      if (!this.range) {
        this.startingDate = event.date
        return
      }
      if (this.selectingTurn === SELECT_STARTING_TURN) {
        this.selectingTurn = SELECT_ENDING_TURN
        this.startingDate = event.date
        this.endingDate = this.startingDate // clear end by set it to startingDate
      } else {
        this.selectingTurn = SELECT_STARTING_TURN

        // set start and clear end if selecting date before start
        if (event.date < this.startingDate) {
          this.startingDate = event.date
          this.endingDate = this.startingDate
          this.selectingTurn = SELECT_ENDING_TURN
        } else {
          this.endingDate = event.date
          this.diffDays = differenceInCalendarDays(
            this.endingDate,
            this.startingDate
          )
        }
      }
    },

    generateMonths(date) {
      this.months = []
      for (let m = 0; m < this.showMonths; m++) {
        this.getDaysOfMonth(addMonths(date, m))
      }
    },

    getDaysOfMonth(firstDateOfMonth) {
      // Day of week (0: sun, 1: mon, ...)
      const firstDayOfMonthInWeek = getDay(firstDateOfMonth)

      const daysOfMonth = []
      // all days before the first day of week is invalid date in calendar
      for (let s = 0; s < firstDayOfMonthInWeek; s++) {
        daysOfMonth.push({ dayNumber: INVALID_DAY })
      }
      // push valid days
      for (let s = 0; s < getDaysInMonth(firstDateOfMonth); s++) {
        const d = addDays(firstDateOfMonth, s)
        daysOfMonth.push({
          dayNumber: s + 1,
          date: d,
          isWeekend: isWeekend(d),
        })
      }
      // fill last week
      const remainDaysInLastWeek = daysOfMonth.length % 7
      if (remainDaysInLastWeek > 0) {
        for (let s = 0; s < 7 - remainDaysInLastWeek; s++) {
          daysOfMonth.push({ dayNumber: INVALID_DAY })
        }
      }

      const daysInMonth = {
        month: parseInt(format(firstDateOfMonth, 'M')),
        year: parseInt(format(firstDateOfMonth, 'yyyy')),
        days: daysOfMonth,
        firstDayOfWeek: firstDayOfMonthInWeek,
      }
      this.months.push(daysInMonth)
    },

    // render next 1 month, that's different with current prod system
    renderNextMonth: throttle(function () {
      const firstActiveMonth = this.months[0]
      // shift 1 month, don't +1 due to Date api month start from 0
      const firstDateOfNextMonth = new Date(
        firstActiveMonth.year,
        firstActiveMonth.month,
        1
      )
      this.generateMonths(firstDateOfNextMonth)

      // fire event
      const lastActiveMonth = this.months[this.months.length - 1]
      this.$emit('next-month', {
        year: lastActiveMonth.year,
        month: lastActiveMonth.month,
      })
    }, 200),

    renderPreviousMonth: throttle(function () {
      const firstActiveMonth = this.months[0]
      // shift << 1 month, Date api month start from 0
      const firstDateOfPreviousMonth = new Date(
        firstActiveMonth.year,
        firstActiveMonth.month - 2,
        1
      )
      this.generateMonths(firstDateOfPreviousMonth)

      this.$emit('previous-month', {
        year: this.months[0].year,
        month: this.months[0].month,
      })
    }, 200),
  },
}
</script>

<style lang="scss">
@import '~@ota-frontend/ui/src/scss/components/modalCalendar.scss';
</style>
